"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ArrangeAnnotations = void 0;
exports.distributeLabelsVertically = distributeLabelsVertically;
exports.sortAnnotationsByYCoord = sortAnnotationsByYCoord;
exports.labelHeight = labelHeight;
exports.moveLabelTowardsParent = moveLabelTowardsParent;
var tslib_1 = require("tslib");
var model_1 = require("../model");
var draw_state_1 = require("./draw.state");
var label_draw_svg_1 = require("./drawable/label.draw.svg");
/**
 * ArrangeAnnotations moves the Label drawables on a given {@link IkeDraw} so that
 * they are neatly arranged without overlapping.
 * @param draw The IkeDraw to arrange labels for
 * @param renderOptions The SVGRenderOptions currently being used to render the IkeDraw
 * @param margin How many pixels to place between adjacent labels
 * @param iterations Optional. The number of times to run the arrangement algorithm (defaults to 100)
 */
var ArrangeAnnotations = /** @class */ (function () {
    function ArrangeAnnotations(draw, renderOptions, margin, iterations) {
        if (margin === void 0) { margin = 20; }
        if (iterations === void 0) { iterations = 100; }
        this.draw = draw;
        this.renderOptions = renderOptions;
        this.margin = margin;
        this.iterations = iterations;
    }
    Object.defineProperty(ArrangeAnnotations.prototype, "markersWithLabels", {
        get: function () {
            return this.draw.drawState.getAllDrawables()
                .filter(function (d) { return model_1.CollectionModelDrawableMarker.isMarker(d) || model_1.CollectionModelDrawableMarkerOffset.isBaseOffset(d); })
                .filter(function (m) { return m.label; });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ArrangeAnnotations.prototype, "linesWithLabels", {
        get: function () {
            return this.draw.drawState.getAllDrawables()
                .filter(function (d) { return model_1.CollectionModelDrawableLine.isLine(d); })
                .filter(function (m) { return m.label; });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ArrangeAnnotations.prototype, "verticalSeparations", {
        get: function () {
            return this.draw.drawState.getAllDrawables()
                .filter(function (d) { return d instanceof model_1.CollectionModelDrawableVerticalSeparation; });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ArrangeAnnotations.prototype, "labels", {
        get: function () {
            return tslib_1.__spreadArray(tslib_1.__spreadArray(tslib_1.__spreadArray([], tslib_1.__read(this.markersWithLabels.map(function (m) { return m.label; })), false), tslib_1.__read(this.linesWithLabels.map(function (m) { return m.label; })), false), tslib_1.__read(this.verticalSeparations.map(function (m) { return m.label; })), false);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ArrangeAnnotations.prototype, "drawableWithLabelsOnLeftSide", {
        get: function () {
            var leftSideMarkers = this.markersWithLabels.filter(function (m) { return !m.isInsightMeasurement && m.label.isOnLeftSide(); });
            var insightDetectionsNotWires = this.markersWithLabels.filter(function (m) {
                var _a;
                return m instanceof model_1.CollectionModelDrawableMarker
                    && m.isInsightMeasurement
                    && ((_a = m.insight) === null || _a === void 0 ? void 0 : _a.detection_class) !== 'wire';
            });
            var leftSideVertSep = this.verticalSeparations.filter(function (d) { return d.label.isOnLeftSide(); });
            return tslib_1.__spreadArray(tslib_1.__spreadArray(tslib_1.__spreadArray([], tslib_1.__read(leftSideMarkers), false), tslib_1.__read(insightDetectionsNotWires), false), tslib_1.__read(leftSideVertSep), false);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ArrangeAnnotations.prototype, "drawablesWithLabelsOnRightSide", {
        get: function () {
            var rightSideMarkers = this.markersWithLabels.filter(function (m) { return !m.isInsightMeasurement && m.label.isOnRightSide(); });
            var insightDetectionsWires = this.markersWithLabels.filter(function (m) {
                var _a;
                return m instanceof model_1.CollectionModelDrawableMarker
                    && m.isInsightMeasurement
                    && ((_a = m.insight) === null || _a === void 0 ? void 0 : _a.detection_class) === 'wire';
            });
            var rightSideVertSep = this.verticalSeparations.filter(function (d) { return d.label.isOnRightSide(); });
            return tslib_1.__spreadArray(tslib_1.__spreadArray(tslib_1.__spreadArray(tslib_1.__spreadArray([], tslib_1.__read(rightSideMarkers), false), tslib_1.__read(insightDetectionsWires), false), tslib_1.__read(this.linesWithLabels), false), tslib_1.__read(rightSideVertSep), false);
        },
        enumerable: false,
        configurable: true
    });
    ArrangeAnnotations.prototype.run = function () {
        var e_1, _a, e_2, _b, e_3, _c;
        try {
            for (var _d = tslib_1.__values(this.drawableWithLabelsOnLeftSide), _e = _d.next(); !_e.done; _e = _d.next()) {
                var leftMarker = _e.value;
                leftMarker.label.snapToLeftSide();
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_e && !_e.done && (_a = _d.return)) _a.call(_d);
            }
            finally { if (e_1) throw e_1.error; }
        }
        try {
            for (var _f = tslib_1.__values(this.drawablesWithLabelsOnRightSide), _g = _f.next(); !_g.done; _g = _f.next()) {
                var rightMarker = _g.value;
                rightMarker.label.snapToRightSide();
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (_g && !_g.done && (_b = _f.return)) _b.call(_f);
            }
            finally { if (e_2) throw e_2.error; }
        }
        distributeLabelsVertically(this.draw, this.renderOptions, this.drawableWithLabelsOnLeftSide);
        distributeLabelsVertically(this.draw, this.renderOptions, this.drawablesWithLabelsOnRightSide);
        for (var i = 0; i < this.iterations; i++) {
            // Alternate between moving labels in regular and reverse order for each pass.
            var labels = i % 2 === 0 ? this.labels : this.labels.reverse();
            var _loop_1 = function (label) {
                var sameSideLabels = label.isOnLeftSide() ? this_1.drawableWithLabelsOnLeftSide : this_1.drawablesWithLabelsOnRightSide;
                var otherLabels = sameSideLabels.map(function (annotation) { return annotation.label; }).filter(function (other) { return other !== label; });
                if (label.parent) {
                    moveLabelTowardsParent(label, label.parent, otherLabels, this_1.draw, this_1.renderOptions, this_1.margin);
                }
            };
            var this_1 = this;
            try {
                for (var labels_1 = (e_3 = void 0, tslib_1.__values(labels)), labels_1_1 = labels_1.next(); !labels_1_1.done; labels_1_1 = labels_1.next()) {
                    var label = labels_1_1.value;
                    _loop_1(label);
                }
            }
            catch (e_3_1) { e_3 = { error: e_3_1 }; }
            finally {
                try {
                    if (labels_1_1 && !labels_1_1.done && (_c = labels_1.return)) _c.call(labels_1);
                }
                finally { if (e_3) throw e_3.error; }
            }
        }
    };
    return ArrangeAnnotations;
}());
exports.ArrangeAnnotations = ArrangeAnnotations;
/**
 * Distribute the given labels of the given markers/lines vertically so that there is
 * an equal amount of space between each label on the Y-axis.
 * The top and bottom labels will touch the top and bottom edge of the image respectively.
 * The labels will be sorted on the Y axis in the same order as their corresponding parents.
 * @param capture The image containing the labels
 * @param annotations The markers/lines whose labels will be distributed vertically
 */
function distributeLabelsVertically(draw, renderOptions, annotations) {
    var e_4, _a, e_5, _b;
    // Do not attempt to distribute if less than two labels are present
    if (annotations.length < 2) {
        return;
    }
    try {
        // Set the Y coord of each label to be equal to their corresponding marker
        for (var annotations_1 = tslib_1.__values(annotations), annotations_1_1 = annotations_1.next(); !annotations_1_1.done; annotations_1_1 = annotations_1.next()) {
            var annotation = annotations_1_1.value;
            annotation.label.move(annotation.label.x, annotation.getLabelConnectionPoint(renderOptions).y);
        }
    }
    catch (e_4_1) { e_4 = { error: e_4_1 }; }
    finally {
        try {
            if (annotations_1_1 && !annotations_1_1.done && (_a = annotations_1.return)) _a.call(annotations_1);
        }
        finally { if (e_4) throw e_4.error; }
    }
    // Calculate the margin to place between each label
    var sumOfLabelHeights = annotations.reduce(function (sum, m) { return sum + labelHeight(draw, renderOptions, m.label); }, 0);
    var margin = (draw.capture.resolution.height - sumOfLabelHeights) / (annotations.length - 1);
    // Sort the markers/lines (and their labels) by their Y coordinates,
    // so that we can iterate through them from highest to lowest vertical position
    var sortedAnnotations = sortAnnotationsByYCoord(annotations);
    var offsetFromTop = 0;
    try {
        // Distribute the labels with equal margin between them.
        // The labels will now be sorted on the Y axis in the same order as their corresponding markers.
        for (var _c = tslib_1.__values(sortedAnnotations.map(function (m) { return m.label; })), _d = _c.next(); !_d.done; _d = _c.next()) {
            var label = _d.value;
            label.move(label.x, Math.round(offsetFromTop));
            offsetFromTop += labelHeight(draw, renderOptions, label) + margin;
        }
    }
    catch (e_5_1) { e_5 = { error: e_5_1 }; }
    finally {
        try {
            if (_d && !_d.done && (_b = _c.return)) _b.call(_c);
        }
        finally { if (e_5) throw e_5.error; }
    }
}
function sortAnnotationsByYCoord(annotations) {
    return tslib_1.__spreadArray([], tslib_1.__read(annotations), false).sort(function (a, b) {
        var aY = a.y;
        var bY = b.y;
        if (model_1.CollectionModelDrawableLine.isLine(a)) {
            aY = a.end.getVector().y;
        }
        if (model_1.CollectionModelDrawableLine.isLine(b)) {
            bY = b.end.getVector().y;
        }
        return aY - bY;
    });
}
function labelHeight(draw, renderOptions, label) {
    if (!label.parent) {
        return label_draw_svg_1.LabelSVGDraw.height(1) * draw_state_1.DEFAULT_SCALE;
    }
    var textLines = draw.getLabelText(label.parent.id, renderOptions);
    return label_draw_svg_1.LabelSVGDraw.height(textLines.length) * draw_state_1.DEFAULT_SCALE;
}
/**
 * Move a label towards a given marker/line vertically until it either hits another label,
 * or becomes vertically centered at the Y-coordinate of the marker/line.
 * @param label The label to move
 * @param parent The marker/line to move towards
 */
function moveLabelTowardsParent(label, parent, otherLabels, draw, renderOptions, margin) {
    var parentY = parent.getLabelConnectionPoint(renderOptions).y;
    if (label.y === parentY) {
        return;
    }
    var direction = label.y - parentY > 0 ? 'up' : 'down';
    var closestLabel = direction === 'up'
        ? getClosestLabelUp(label, otherLabels, draw, renderOptions)
        : getClosestLabelDown(label, otherLabels, draw, renderOptions);
    var targetPos = direction === 'up'
        ? getTargetPositionUp(closestLabel, label, parent, margin, draw, renderOptions)
        : getTargetPositionDown(closestLabel, label, parent, margin, draw, renderOptions);
    label.move(label.x, targetPos);
}
function getTargetPositionUp(closestLabel, label, parent, margin, draw, renderOptions) {
    var targetPosY = parent.getLabelConnectionPoint(renderOptions).y;
    if (!closestLabel) {
        return targetPosY;
    }
    var targetClosestLabelHeight = closestLabel.y + labelHeight(draw, renderOptions, closestLabel) + margin;
    if (label.y - targetClosestLabelHeight < label.y - targetPosY) {
        return targetClosestLabelHeight;
    }
    return Math.max(0, targetPosY);
}
function getTargetPositionDown(closestLabel, label, parent, margin, draw, renderOptions) {
    var height = labelHeight(draw, renderOptions, label);
    // If the parent is near the bottom edge of the image, only slide to the bottom edge of the image if that's closer
    var targetPosY = Math.min(draw.capture.resolution.height - labelHeight(draw, renderOptions, label), parent.getLabelConnectionPoint(renderOptions).y);
    if (!closestLabel) {
        return targetPosY;
    }
    var targetClosestLabelHeight = closestLabel.y - height - margin;
    // If the distance between the bottom of the label and the top of the closest label (plus a margin) is
    // less than the distance between the bottom of the label and the parent, then target the top of the closest label (plus a margin).
    if (targetClosestLabelHeight - label.y < targetPosY - label.y) {
        return targetClosestLabelHeight;
    }
    return targetPosY;
}
function getClosestLabelUp(label, otherLabels, draw, renderOptions) {
    var e_6, _a;
    var closest = null;
    var closestBottom = null;
    try {
        for (var otherLabels_1 = tslib_1.__values(otherLabels), otherLabels_1_1 = otherLabels_1.next(); !otherLabels_1_1.done; otherLabels_1_1 = otherLabels_1.next()) {
            var otherLabel = otherLabels_1_1.value;
            var otherLabelBottom = labelHeight(draw, renderOptions, otherLabel) + otherLabel.y;
            if (otherLabelBottom > label.y) {
                // Other label is overlapping or below, so skip it.
                continue;
            }
            if (!closest || !closestBottom) {
                closest = otherLabel;
                closestBottom = otherLabelBottom;
                continue;
            }
            if (otherLabelBottom < label.y && otherLabelBottom > closestBottom) {
                closest = otherLabel;
                closestBottom = otherLabelBottom;
            }
        }
    }
    catch (e_6_1) { e_6 = { error: e_6_1 }; }
    finally {
        try {
            if (otherLabels_1_1 && !otherLabels_1_1.done && (_a = otherLabels_1.return)) _a.call(otherLabels_1);
        }
        finally { if (e_6) throw e_6.error; }
    }
    return closest;
}
function getClosestLabelDown(label, otherLabels, draw, renderOptions) {
    var e_7, _a;
    var bottom = labelHeight(draw, renderOptions, label) + label.y;
    var closest = null;
    try {
        for (var otherLabels_2 = tslib_1.__values(otherLabels), otherLabels_2_1 = otherLabels_2.next(); !otherLabels_2_1.done; otherLabels_2_1 = otherLabels_2.next()) {
            var otherLabel = otherLabels_2_1.value;
            if (otherLabel.y < bottom) {
                // Other label is overlapping or below, so skip it.
                continue;
            }
            if (!closest) {
                closest = otherLabel;
                continue;
            }
            if (otherLabel.y > bottom && otherLabel.y < closest.y) {
                closest = otherLabel;
            }
        }
    }
    catch (e_7_1) { e_7 = { error: e_7_1 }; }
    finally {
        try {
            if (otherLabels_2_1 && !otherLabels_2_1.done && (_a = otherLabels_2.return)) _a.call(otherLabels_2);
        }
        finally { if (e_7) throw e_7.error; }
    }
    return closest;
}
//# sourceMappingURL=ArrangeAnnotations.js.map